import { Injectable } from '@angular/core';
import { InMemoryDbService, RequestInfo } from 'angular-in-memory-web-api';
import { dummyCrudSearchPubMockData } from '../dummy-crud/dummy-crud.mock';

@Injectable({
  providedIn: 'root',
})
export class MockDataService implements InMemoryDbService {
  // In-memory storage for active OTP sessions
  private activeOTPSessions: {
    [transactionId: string]: {
      otpCode: string,
      beneficiaryId: string,
      prescriptionId: string,
      expiresAt: number,
      retries: number
    }
  } = {};

  createDb() {
    // Mock collections (like database tables)
    const dummyCrud = dummyCrudSearchPubMockData;

    // Verification records for tracking
    const verificationRecords = [
      {
        id: '1',
        prescriptionId: 'RX-2024-001',
        beneficiaryId: '1',
        beneficiaryName: 'Γιάννης Παπαδόπουλος',
        pharmacyId: 'PHARM001',
        pharmacyName: 'Φαρμακείο Κεντρικό',
        verificationCode: 'TP-2024-001-001',
        verificationMethod: 'OTP',
        verifiedBy: 'pharmacy',
        timestamp: new Date(),
        status: 'VERIFIED'
      }
    ];

    // Employee management data
    const employees = [
      {
        id: '1',
        username: 'employee1',
        name: 'Άννα',
        surname: 'Δημητρίου',
        email: 'anna@pharmacy.com',
        role: 'Employee',
        pharmacyId: 'PHARM001',
        status: 'ACTIVE',
        createdBy: 'pharmacy',
        createdDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
      },
      {
        id: '2',
        username: 'pharmacy',
        name: 'Μαρία',
        surname: 'Φαρμακοποιός',
        email: 'pharmacy@pharmacy.com',
        role: 'Pharmacist',
        pharmacyId: 'PHARM001',
        status: 'ACTIVE',
        createdBy: 'system',
        createdDate: new Date(Date.now() - 60 * 24 * 60 * 60 * 1000)
      }
    ];

    // Return an object with multiple collections
    return {
      dummyCrud,
      verificationRecords,
      employees,
    };
  }

  // Override the post method to handle authentication and prescriptions
  post(reqInfo: RequestInfo) {
    const { collectionName, req, utils } = reqInfo;

    // Handle prescription search (internal API only)
    if (req.url.includes('prescriptions/search') && req.url.includes('/api/v1/')) {
      const body = reqInfo.utils.getJsonBody(reqInfo.req) || {};
      const prescriptionCode = body.prescriptionCode;

      // Mock prescription data based on test barcodes
      const mockPrescriptions: any = {
        '1234567890123': {
          id: '1',
          prescriptionCode: '1234567890123',
          prescriptionId: 'RX-2024-001',
          retrievedDate: new Date(),
          status: 'ACTIVE',
          issuedDate: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000),
          expiryDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
          doctorName: 'Δρ. Μαρία Κωνσταντίνου',
          doctorSpecialty: 'Παθολόγος',
          beneficiaries: [{
            id: '1',
            amka: '12345678901',
            name: 'Γιάννης',
            surname: 'Παπαδόπουλος',
            phone: '6900000000',
            email: 'giannis@example.com',
            dateOfBirth: new Date('1980-05-15'),
            isPrimary: true,
            hasValidPhone: true,
            eligibleForOTP: true
          }, {
            id: '1b',
            amka: '12345678902',
            name: 'Ελένη',
            surname: 'Παπαδοπούλου',
            phone: '6900000001',
            email: 'eleni@example.com',
            dateOfBirth: new Date('1985-03-22'),
            isPrimary: false,
            hasValidPhone: true,
            eligibleForOTP: true
          }],
          medications: [{
            id: '1',
            name: 'Paracetamol 500mg',
            dosage: '500mg',
            quantity: '20 δισκία',
            instructions: 'Ένα δισκίο κάθε 6 ώρες'
          }]
        },
        '9876543210987': {
          id: '2',
          prescriptionCode: '9876543210987',
          prescriptionId: 'RX-2024-002',
          retrievedDate: new Date(),
          status: 'ACTIVE',
          issuedDate: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000),
          expiryDate: new Date(Date.now() + 29 * 24 * 60 * 60 * 1000),
          doctorName: 'Δρ. Πέτρος Αντωνίου',
          doctorSpecialty: 'Καρδιολόγος',
          beneficiaries: [{
            id: '2',
            amka: '98765432109',
            name: 'Μαρία',
            surname: 'Γεωργίου',
            phone: '6911111111',
            email: 'maria@example.com',
            dateOfBirth: new Date('1975-08-20'),
            isPrimary: true,
            hasValidPhone: true,
            eligibleForOTP: true
          }],
          medications: [{
            id: '2',
            name: 'Atenolol 25mg',
            dosage: '25mg',
            quantity: '30 δισκία',
            instructions: 'Ένα δισκίο το πρωί'
          }]
        },
        '5555555555555': {
          id: '3',
          prescriptionCode: '5555555555555',
          prescriptionId: 'RX-2024-003',
          retrievedDate: new Date(),
          status: 'EXPIRED',
          issuedDate: new Date(Date.now() - 35 * 24 * 60 * 60 * 1000),
          expiryDate: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000),
          doctorName: 'Δρ. Σοφία Νικολάου',
          doctorSpecialty: 'Ορθοπεδικός',
          beneficiaries: [{
            id: '3',
            amka: '55555555555',
            name: 'Πέτρος',
            surname: 'Δημητρίου',
            phone: null,
            email: 'petros@example.com',
            dateOfBirth: new Date('1990-12-10'),
            isPrimary: true,
            hasValidPhone: false,
            eligibleForOTP: false
          }],
          medications: [{
            id: '3',
            name: 'Ibuprofen 400mg',
            dosage: '400mg',
            quantity: '20 δισκία',
            instructions: 'Ένα δισκίο κάθε 8 ώρες μετά το φαγητό'
          }]
        }
      };

      const prescription = mockPrescriptions[prescriptionCode];

      if (prescription) {
        return utils.createResponse$(() => ({
          status: 200,
          body: prescription
        }));
      } else {
        return utils.createResponse$(() => ({
          status: 404,
          body: { message: 'Η συνταγή δεν βρέθηκε. Παρακαλώ ελέγξτε τον κωδικό και προσπαθήστε ξανά.' }
        }));
      }
    }

    // Handle OTP request
    if (req.url.includes('otp/request')) {
      const body = reqInfo.utils.getJsonBody(reqInfo.req) || {};

      // Generate realistic OTP session
      const transactionId = 'OTP-' + Date.now();
      const otpCode = Math.floor(100000 + Math.random() * 900000).toString(); // 6 digit OTP
      const expiresAt = Date.now() + (5 * 60 * 1000); // 5 minutes expiry

      // Store OTP session
      this.activeOTPSessions[transactionId] = {
        otpCode: otpCode,
        beneficiaryId: body.beneficiaryId,
        prescriptionId: body.prescriptionId || '',
        expiresAt: expiresAt,
        retries: 0
      };

      // For demo purposes, log the OTP to console
      console.log(`Mock OTP generated for transaction ${transactionId}: ${otpCode}`);

      return utils.createResponse$(() => ({
        status: 200,
        body: {
          transactionReference: transactionId,
          expiresIn: 300, // 5 minutes in seconds
          retryLimit: 3,
          message: `Ο κωδικός OTP στάλθηκε στο τηλέφωνο του δικαιούχου. [Demo: ${otpCode}]`
        }
      }));
    }

    // Handle OTP validation and verification code generation
    if (req.url.includes('otp/validate')) {
      const body = reqInfo.utils.getJsonBody(reqInfo.req) || {};
      const transactionId = body.transactionReference;
      const otpCode = body.otpCode;

      // Check if transaction exists
      if (!transactionId || !this.activeOTPSessions[transactionId]) {
        return utils.createResponse$(() => ({
          status: 400,
          body: {
            success: false,
            message: 'Μη έγκυρη συνεδρία OTP. Παρακαλώ ζητήστε νέο κωδικό.'
          }
        }));
      }

      const session = this.activeOTPSessions[transactionId];

      // Check if OTP has expired
      if (Date.now() > session.expiresAt) {
        delete this.activeOTPSessions[transactionId];
        return utils.createResponse$(() => ({
          status: 400,
          body: {
            success: false,
            message: 'Ο κωδικός OTP έχει λήξει. Παρακαλώ ζητήστε νέο κωδικό.'
          }
        }));
      }

      // Increment retry count
      session.retries++;

      // Check if correct OTP
      if (otpCode === session.otpCode) {
        // Generate verification code (TP)
        const verificationCode = `TP-${new Date().getFullYear()}-${String(Date.now()).slice(-6)}`;

        // Clean up session
        delete this.activeOTPSessions[transactionId];

        return utils.createResponse$(() => ({
          status: 200,
          body: {
            success: true,
            verified: true,
            verificationCode: verificationCode,
            prescriptionId: session.prescriptionId,
            beneficiaryId: session.beneficiaryId,
            message: 'Η επαλήθευση ολοκληρώθηκε επιτυχώς.'
          }
        }));
      } else {
        // Check retry limit
        if (session.retries >= 3) {
          delete this.activeOTPSessions[transactionId];
          return utils.createResponse$(() => ({
            status: 400,
            body: {
              success: false,
              message: 'Έχετε υπερβεί τον μέγιστο αριθμό προσπαθειών. Παρακαλώ ζητήστε νέο κωδικό OTP.'
            }
          }));
        }

        return utils.createResponse$(() => ({
          status: 400,
          body: {
            success: false,
            message: `Λάθος κωδικός OTP. Έχετε ${3 - session.retries} προσπάθειες ακόμη.`,
            retriesRemaining: 3 - session.retries
          }
        }));
      }
    }

    // Handle manual verification (document upload)
    if (req.url.includes('verification/manual')) {
      const body = reqInfo.utils.getJsonBody(reqInfo.req) || {};

      // Validate file requirements
      const fileName = body.fileName || '';
      const fileSize = body.fileSize || 0;
      const fileType = body.fileType || '';

      // Check file type (PDF, JPG, PNG as per FSD)
      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
      if (!allowedTypes.includes(fileType)) {
        return utils.createResponse$(() => ({
          status: 400,
          body: {
            success: false,
            message: 'Μη επιτρεπτός τύπος αρχείου. Επιτρέπονται μόνο PDF, JPG και PNG αρχεία.'
          }
        }));
      }

      // Check file size (max 10MB)
      if (fileSize > 10 * 1024 * 1024) {
        return utils.createResponse$(() => ({
          status: 400,
          body: {
            success: false,
            message: 'Το αρχείο είναι πολύ μεγάλο. Το μέγιστο επιτρεπτό μέγεθος είναι 10MB.'
          }
        }));
      }

      // Generate verification code for manual verification
      const verificationCode = `TP-${new Date().getFullYear()}-${String(Date.now()).slice(-6)}`;
      const uploadId = `DOC-${Date.now()}`;

      return utils.createResponse$(() => ({
        status: 200,
        body: {
          id: uploadId,
          fileName: fileName,
          filePath: `/uploads/documents/${uploadId}_${fileName}`,
          fileSize: fileSize,
          success: true,
          verified: true,
          verificationCode: verificationCode,
          prescriptionId: body.prescriptionId,
          beneficiaryId: body.beneficiaryId,
          verificationMethod: 'MANUAL',
          uploadedDate: new Date(),
          message: 'Η επικύρωση με έγγραφο ολοκληρώθηκε επιτυχώς.'
        }
      }));
    }

    // Handle authentication login (internal API only)
    if (req.url.includes('Auth/login')) {
      const body = reqInfo.utils.getJsonBody(reqInfo.req) || {};

      // Mock authentication - accept these credentials
      const validCredentials = [
        { username: 'admin', password: 'admin' },
        { username: 'pharmacy', password: 'pharmacy123' },
        { username: 'pharmacist', password: 'pharmacist123' },
        { username: 'employee', password: 'employee123' },
        { username: 'test', password: 'test123' },
        { username: 'user', password: 'password' }
      ];

      const isValid = validCredentials.some(cred =>
        cred.username === body.username && cred.password === body.password
      );

      if (isValid) {
        // Determine role and permissions based on username
        let role = 'User';
        let permissions: string[] = [];

        if (body.username === 'admin') {
          role = 'Admin';
          permissions = [
            'dummyCrud',
            'PRESCRIPTIONS_VIEW',
            'OTP_REQUEST',
            'OTP_VALIDATE',
            'DOCUMENTS_UPLOAD',
            'ADMIN_VIEW',
            'SETTINGS',
            'SETTINGS_UPDATE',
            'ANNOUNCEMENTS',
            'users',
            'CONTACT',
            'ROLE_MANAGEMENT'
          ];
        } else if (body.username === 'pharmacy' || body.username === 'pharmacist') {
          role = 'Pharmacist';
          permissions = [
            'PRESCRIPTIONS_VIEW',
            'PRESCRIPTIONS_VALIDATE',
            'PRESCRIPTIONS_SEARCH',
            'OTP_REQUEST',
            'OTP_VALIDATE',
            'DOCUMENTS_UPLOAD',
            'USER_MANAGEMENT',
            'CONTACT',
            'SETTINGS_UPDATE'
          ];
        } else if (body.username === 'employee') {
          role = 'Employee';
          permissions = [
            'PRESCRIPTIONS_VIEW',
            'PRESCRIPTIONS_VALIDATE',
            'PRESCRIPTIONS_SEARCH',
            'OTP_REQUEST',
            'OTP_VALIDATE',
            'DOCUMENTS_UPLOAD'
          ];
        } else {
          // Regular user permissions
          role = 'Employee';
          permissions = [
            'PRESCRIPTIONS_VIEW',
            'PRESCRIPTIONS_VALIDATE'
          ];
        }

        // Create a proper mock JWT token with the appropriate role and permissions
        const payload = {
          sub: '1234567890',
          name: body.username,
          iat: Math.floor(Date.now() / 1000),
          role: role,
          username: body.username,
          permissions: permissions,
          exp: 9999999999
        };

        // Simple base64 encoding for mock JWT
        const header = btoa(JSON.stringify({ alg: 'HS256', typ: 'JWT' }));
        const payloadEncoded = btoa(JSON.stringify(payload));
        const mockToken = `${header}.${payloadEncoded}.mock_signature`;

        return utils.createResponse$(() => ({
          status: 200,
          body: { token: mockToken }
        }));
      } else {
        return utils.createResponse$(() => ({
          status: 401,
          body: { message: 'Τα στοιχεία χρήστη που πληκτρολογήσατε δεν είναι σωστά. Παρακαλώ προσπαθήστε ξανά.' }
        }));
      }
    }

    // Handle user search for user management
    if (req.url.includes('/v1/Users/search/')) {
      const mockUsers = [
        {
          id: '1',
          userName: 'employee1',
          name: 'Άννα',
          surname: 'Δημητρίου',
          email: 'anna@pharmacy.com',
          role: 'Employee',
          lockoutEnabled: false,
          emailConfirmed: true,
          changePasswordOnNextUserLogin: false,
          passwordNotExpires: true,
          roles: ['Employee']
        },
        {
          id: '2',
          userName: 'pharmacy',
          name: 'Μαρία',
          surname: 'Φαρμακοποιός',
          email: 'pharmacy@pharmacy.com',
          role: 'Pharmacist',
          lockoutEnabled: false,
          emailConfirmed: true,
          changePasswordOnNextUserLogin: false,
          passwordNotExpires: true,
          roles: ['Pharmacist']
        }
      ];

      return utils.createResponse$(() => ({
        status: 200,
        body: {
          resultset: mockUsers,
          totalCount: mockUsers.length,
          pageNumber: 1,
          pageSize: 10
        }
      }));
    }

    // For all other requests, use default behavior
    return undefined;
  }

  // Override the get method to handle prescription details
  get(reqInfo: RequestInfo) {
    const { req, utils } = reqInfo;

    // Handle audit dashboard data
    if (req.url.includes('admin/verifications') || req.url.includes('audit/verifications')) {
      // Return mock verification records for admin dashboard
      const mockAuditData = [
        {
          id: '1',
          prescriptionId: 'RX-2024-001',
          beneficiaryName: 'Γιάννης Παπαδόπουλος',
          pharmacyName: 'Φαρμακείο Κεντρικό',
          verificationCode: 'TP-2024-001-001',
          verificationMethod: 'OTP',
          verifiedBy: 'pharmacy',
          timestamp: new Date(),
          status: 'VERIFIED'
        },
        {
          id: '2',
          prescriptionId: 'RX-2024-002',
          beneficiaryName: 'Μαρία Γεωργίου',
          pharmacyName: 'Φαρμακείο Κεντρικό',
          verificationCode: 'TP-2024-002-001',
          verificationMethod: 'MANUAL',
          verifiedBy: 'employee',
          timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000),
          status: 'VERIFIED'
        }
      ];

      return utils.createResponse$(() => ({
        status: 200,
        body: { data: mockAuditData, total: mockAuditData.length }
      }));
    }

    // Handle employee management
    if (req.url.includes('employees')) {
      const mockEmployees = [
        {
          id: '1',
          username: 'employee1',
          name: 'Άννα',
          surname: 'Δημητρίου',
          email: 'anna@pharmacy.com',
          role: 'Employee',
          status: 'ACTIVE',
          createdDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
        },
        {
          id: '2',
          username: 'pharmacy',
          name: 'Μαρία',
          surname: 'Φαρμακοποιός',
          email: 'pharmacy@pharmacy.com',
          role: 'Pharmacist',
          status: 'ACTIVE',
          createdDate: new Date(Date.now() - 60 * 24 * 60 * 60 * 1000)
        }
      ];

      return utils.createResponse$(() => ({
        status: 200,
        body: { data: mockEmployees, total: mockEmployees.length }
      }));
    }

    // Handle prescription details by ID (internal API only)
    if (req.url.includes('prescriptions/') && !req.url.includes('search') && req.url.includes('/api/v1/')) {
      const id = req.url.split('/').pop();

      const mockPrescriptionDetails: any = {
        '1': {
          id: '1',
          prescriptionCode: '1234567890123',
          prescriptionId: 'RX-2024-001',
          retrievedDate: new Date(),
          status: 'ACTIVE',
          beneficiaries: [{
            id: '1',
            amka: '12345678901',
            name: 'Γιάννης',
            surname: 'Παπαδόπουλος',
            phone: '6900000000',
            email: 'giannis@example.com',
            dateOfBirth: new Date('1980-05-15'),
            isPrimary: true
          }, {
            id: '1b',
            amka: '12345678902',
            name: 'Ελένη',
            surname: 'Παπαδοπούλου',
            phone: '6900000001',
            email: 'eleni@example.com',
            dateOfBirth: new Date('1985-03-22'),
            isPrimary: false
          }]
        }
      };

      const prescription = mockPrescriptionDetails[id || ''];

      if (prescription) {
        return utils.createResponse$(() => ({
          status: 200,
          body: prescription
        }));
      } else {
        return utils.createResponse$(() => ({
          status: 404,
          body: { message: 'Η συνταγή δεν βρέθηκε.' }
        }));
      }
    }

    // Handle external verification API
    if (req.url.includes('/api/verification/') && !req.url.includes('manual')) {
      const prescriptionId = req.url.split('/').pop();

      // Mock verification status - some prescriptions are verified
      const mockVerificationStatus: any = {
        'RX-2024-001': {
          verified: true,
          verificationCode: 'TP-2024-001-001'
        },
        'RX-2024-002': {
          verified: true,
          verificationCode: 'TP-2024-002-001'
        },
        'RX-2024-003': {
          verified: false,
          verificationCode: null
        }
      };

      const status = mockVerificationStatus[prescriptionId || ''] || {
        verified: false,
        verificationCode: null
      };

      return utils.createResponse$(() => ({
        status: 200,
        body: status
      }));
    }

    // For all other requests, use default behavior
    return undefined;
  }
}